'use client';

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { ProductFile } from '@eliadmin/core/src/api/products/types/product-types';
import { SIGN_IN_DIALOG } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { useOpenStaySignIn } from '@eliadmin/core/src/components/nt/shared/stay-sign-in-dialog';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

import { WriteReviewDialog } from './write-review-dialog';
import type { WriteReviewTexts } from './write-review-texts';

export type WriteReviewProduct = { slug: string; name: string; image: ProductFile | null };

type WriteReviewState = {
  /** „Napísať hodnotenie“: the modal, or the sign-in first (the modal opens once signed in). */
  write: () => void;
  /** „Prihlásiť sa“ without the intent to write right away. */
  signIn: () => void;
  signedIn: boolean;
};

const WriteReviewContext = createContext<WriteReviewState | null>(null);

/**
 * Write-a-review flow of the reviews tab (Figma 146:4970): the API takes reviews of signed-in
 * customers only (`POST /store/products/{slug}/write-review` with the customer token), so a guest
 * signs in first in the stay-on-page sign-in dialog and the modal follows when the sign-in succeeds.
 */
export function WriteReviewProvider({
  product,
  texts,
  children,
}: {
  product: WriteReviewProduct;
  texts: WriteReviewTexts;
  children: ReactNode;
}) {
  const me = useCustomerStore(state => state.me);
  const openSignIn = useOpenStaySignIn();
  const signInOpen = useDialogStore(state => SIGN_IN_DIALOG in state.dialogs);
  const [open, setOpen] = useState(false);
  /** „Napísať hodnotenie“ asked for while signed out – kept until the sign-in dialog closes. */
  const [pending, setPending] = useState(false);
  const signedIn = !!me?.authToken;

  useEffect(() => {
    if (!pending) return;
    if (signedIn) {
      setPending(false);
      setOpen(true);
    } else if (!signInOpen) setPending(false);
  }, [pending, signedIn, signInOpen]);

  const value = useMemo<WriteReviewState>(
    () => ({
      write: () => {
        if (signedIn) return setOpen(true);
        setPending(true);
        openSignIn();
      },
      signIn: () => {
        setPending(false);
        openSignIn();
      },
      signedIn,
    }),
    [openSignIn, signedIn],
  );

  return (
    <WriteReviewContext.Provider value={value}>
      {children}
      <WriteReviewDialog open={open} onOpenChange={setOpen} product={product} texts={texts} />
    </WriteReviewContext.Provider>
  );
}

export function useWriteReview() {
  const state = useContext(WriteReviewContext);
  if (!state) throw new Error('useWriteReview must be used inside WriteReviewProvider');
  return state;
}
